import {
    AfterViewInit,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    effect,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { FileSaverService } from 'apps/account/src/account-shared/services/fileSaver.service';
import { FtModalComponent } from '../../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../../common/components/modal/ft-modal-chrome.component';
import { CurationBackgroundImageDirective } from '../../../stories/curation-card/background-image.directive';
import { ShareOAuthService } from '../../../stories/broadcast/share-oauth.service';

/**
 * Sized share-image preview iframe for the invitation-share modal — writes the
 * template HTML into the iframe (document.write), sized per network viewport
 * (FB/TW 1200×630, LinkedIn 1200×627). The host calls `rewrite()` on the two
 * back-to-editor re-shows: the overlay detach discards the iframe's browsing
 * context and no input change would trigger a lifecycle hook to re-write it.
 */
@Component({
    selector: 'ft-invitation-share-iframe-ng',
    imports: [],
    templateUrl: './ft-invitation-share-iframe.component.html',
    styleUrls: ['./ft-invitation-share-iframe.component.scss'],
})
export class FtInvitationShareIframeComponent implements AfterViewInit {
    readonly content = input('');
    readonly socialNetworkType = input<any>();

    private readonly frame = viewChild<ElementRef<HTMLIFrameElement>>('frame');
    private viewReady = false;

    constructor() {
        // Skip the pre-view first-bind cycle so creation writes ONCE (v1
        // setPreviewContent ran once per trigger; the FtModalComponent pattern).
        effect(() => {
            this.content();
            this.socialNetworkType();
            if (this.viewReady) this.write();
        });
    }

    ngAfterViewInit(): void {
        this.viewReady = true;
        this.write();
    }

    /**
     * Re-run the v1 `setPreviewContent` — the host calls this on the two
     * back-to-editor re-shows (ready-modal Cancel, create-image failure): the
     * overlay detach discards the iframe's browsing context, and since neither
     * input changes, no lifecycle hook would re-write it.
     */
    rewrite(): void {
        this.write();
    }

    private write(): void {
        const content = this.content();
        if (!content) {
            return;
        }
        // v1 deferred via $timeout; also lets the *ngIf-created iframe land first.
        setTimeout(() => {
            const iframe = this.frame()?.nativeElement;
            if (!iframe || !iframe.contentWindow) return; // v1 setPreviewContent guard
            // v1 getViewportBySocialNetwork — loose == kept; only 1/2/3 are reachable
            // (the launcher's networks map), so the undefined branch never fires.
            const viewport = this.getViewportBySocialNetwork(this.socialNetworkType());
            if (viewport) {
                iframe.style.width = viewport.width + 'px';
                iframe.style.height = viewport.height + 'px';
            }
            iframe.contentWindow.document.open();
            iframe.contentWindow.document.write(content);
            iframe.contentWindow.document.close();
        });
    }

    private getViewportBySocialNetwork(socialNetworkType: any): { width: number; height: number } | undefined {
        if (socialNetworkType == 1) {
            // Facebook
            return { width: 1200, height: 630 };
        } else if (socialNetworkType == 2) {
            // Twitter
            return { width: 1200, height: 630 };
        } else if (socialNetworkType == 3) {
            // LinkedIn
            return { width: 1200, height: 627 };
        }
        return undefined;
    }
}

/**
 * Native port of the v1 invitation-share modal flow: the editor, create-image
 * progress, and share-image-created steps (the v1 hide/show chains collapsed into
 * one `activeModal` state), each a native ft-modal-overlay + ft-modal-chrome-ng.
 * v1 behavior is preserved verbatim, including documented preserve-bugs — see the
 * per-method comments below.
 */
@Component({
    selector: 'ft-contest-invitation-share-ng',
    imports: [
        FormsModule,
        TranslateModule,
        FtModalComponent,
        FtModalChromeComponent,
        CurationBackgroundImageDirective,
        FtInvitationShareIframeComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-contest-invitation-share.component.html',
})
export class FtContestInvitationShareComponent implements OnInit, OnDestroy {
    /** v1 isolate-scope bindings (invitationShareModal.directive.js:17-27). */
    readonly contestUuid = input<any>();
    readonly companyCode = input<any>();
    readonly property = input<any>();
    /** The contest NAME — bound by the v1 service but never used by the directive (kept for fidelity). */
    readonly contest = input<any>();
    readonly network = input<any>();
    readonly invitationBackground = input<any>();
    /** Only reachable via the commented-out v1 background toggle — kept for fidelity. */
    readonly propertyBackground = input<any>();
    readonly invitationUrl = input<any>();
    readonly languageCode = input<any>();

    /** The launcher destroys the body-mounted host when this fires (v1 scope.$destroy → ftModal.destroy ×3). */
    readonly closed = output<void>();

    /** The sized preview iframe (persists across overlay detach/attach — see rewrite()). */
    private readonly previewIframe = viewChild(FtInvitationShareIframeComponent);
    /** The editor's template-driven form (persists across overlay detach/attach). */
    private readonly editorForm = viewChild<NgForm>('modalForm');

    /** Which of the three v1 ftModals is shown (the hide().then(show()) chains collapsed). */
    protected readonly activeModal = signal<'invitationShare' | 'createImageProgress' | 'shareImageCreated' | null>(null);

    protected readonly model = signal<any>({ isLoadingTemplate: false });
    protected readonly sharing = signal<any>({ isInvitationBackground: true });

    private readonly photoContest = inject(PhotoContestApiService);
    private readonly parse = inject(ParseService);
    private readonly fileSaver = inject(FileSaverService);
    private readonly shareOAuth = inject(ShareOAuthService);
    private readonly translateService = inject(TranslateService);
    private readonly zone = inject(NgZone);

    ngOnInit(): void {
        // ngStrap put these on <body> while a modal was shown (scroll-lock parity, the 2l precedent).
        document.body.classList.add('modal-open', 'modal-with-am-fade');

        // v1 defaultMessages[scope.network.title] — the SAME string for facebook/twitter/linkedin.
        this.sharing().message = this.translate('flipto.account.ContestInvitationDefaultShareMessage');
        // v1 deep $watch on sharing.isInvitationBackground — the toggle UI is commented
        // out in the v1 template, so it always resolves to the invitation background.
        this.sharing().background = this.invitationBackground();

        // v1 service: ftModal.show('invitationShare') on the 'modal-invitationShare.create' broadcast.
        this.activeModal.set('invitationShare');
        // v1 ftModal focusFirstInput (default true) focused the first input/textarea
        // on EVERY 'modal-invitationShare.show' (modal.js:345-352 → focus.firstInputOf).
        this.focusFirstInput();

        // v1 link-fn ran this immediately (directive.js:158).
        this.reloadImageTemplate();

        // v1 registered this at link time and never deregistered it (directive.js:160-174).
        (window as any).fliptoInvitationShareComplete = (state: any) => {
            this.shareOAuth.log('invitation-share', { action: 'complete', state });
            // The popup callback arrives out-of-zone (v1 used scope.$apply).
            this.zone.run(() => {
                if (state == 0 || state == false) {
                    window.alert(this.translate('flipto.account.CurationShareError'));
                } else if (state == 1 || state == true) {
                    window.alert(this.translate('flipto.account.ShareSuccess'));
                }
                this.shareOAuth.close();
                // v1: hide('invitationShare.shareImageCreated') → get('invitationShare').cancel()
                // → 'modal-invitationShare.cancel' → the service scope dies. Net: full teardown
                // regardless of share success/failure.
                this.closed.emit();
            });
        };
    }

    ngOnDestroy(): void {
        document.body.classList.remove('modal-open', 'modal-with-am-fade');
    }

    /** The body footer's PreviewPost — v1 modalScope.done(): onDone=createShareImage returns
     *  undefined (not its promise) → the editor hides immediately either way. */
    protected done(): void {
        this.createShareImage();
    }

    /** The body footer's Cancel — v1 modalScope.cancel() → hide → 'modal-invitationShare.cancel'
     *  → the service destroys the scope (all three modals + inProgress=false). */
    protected cancel(): void {
        this.closed.emit();
    }

    /** The ready modal's Cancel — v1 on-cancel="showInvitationShareModal" → ftModal.show
     *  ('invitationShare').then(setPreviewContent) (directive.js:131-136). The editor's overlay
     *  detach discarded the iframe's browsing context, so the explicit re-write (and the v1
     *  re-show focus) must re-run here. */
    protected cancelShareImageCreated(): void {
        this.activeModal.set('invitationShare');
        this.resetEditorFormState();
        this.previewIframe()?.rewrite();
        this.focusFirstInput();
    }

    /** Verbatim v1 createShareImage (directive.js:54-91). */
    createShareImage(): void {
        const sharing = this.sharing(),
            network = this.network();

        this.activeModal.set('createImageProgress');
        const model = {
            SocialApplication: this.parse.int(network.socialNetworkType),
            /*SelectedPhotoUrl: sharing.background,*/
            ContestUUID: this.contestUuid(),
            LanguageCode: this.languageCode(),
            Template: this.sharing().template,
        };
        this.photoContest.createInvitationShareImageFromTemplate(this.property().UUID, model).then(
            (shareImageUrl: any) => {
                this.zone.run(() => {
                    sharing.shareImageUrl = shareImageUrl;
                    this.activeModal.set('shareImageCreated');
                });
            },
            () => {
                this.zone.run(() => {
                    this.activeModal.set('invitationShare');
                    this.resetEditorFormState();
                    this.focusFirstInput();
                    // v1 (directive.js:78-86): re-show, then `if (!!sharing.template.length)
                    // setPreviewContent` — on undefined template that line THREW before the
                    // $timeout(50) alert was ever scheduled (silent failure) — preserved.
                    if (this.sharing().template == null) return;
                    if (this.sharing().template.length) {
                        this.previewIframe()?.rewrite();
                    }
                    // $timeout(50) so the modal paints before the blocking alert.
                    setTimeout(() => {
                        window.alert(this.translate('flipto.account.CurationShareError'));
                    }, 50);
                });
            },
        );
    }

    /** ngStrap re-LINKED a fresh template clone on every show, so each editor re-show
     *  started with a pristine/untouched form (the typed value re-bound from the model)
     *  — empirically confirmed on the frozen baseline (the failure-re-show textarea is
     *  ng-pristine, flipping the pristine-gated ft-field-error class back on). The
     *  native form instance persists across overlay detach/attach, so reset it. */
    private resetEditorFormState(): void {
        this.editorForm()?.form.markAsPristine();
        this.editorForm()?.form.markAsUntouched();
    }

    /** v1 focus.firstInputOf(elem) — the first input/textarea in the editor modal (the
     *  share-message textarea for twitter/linkedin; a no-op for facebook). Deferred like
     *  the v1 $timeout so the overlay content is attached first. */
    private focusFirstInput(): void {
        setTimeout(() => {
            const el = document.querySelector('#invitationShare input, #invitationShare textarea') as HTMLElement | null;
            el?.focus();
        });
    }

    /** v1 AngularJS default ng-trim: a whitespace-only message trimmed to '' → required
     *  INVALID. Angular required doesn't trim — replicated for the disabled state. */
    protected messageWhitespaceOnly(): boolean {
        return (
            this.network().title !== 'facebook' &&
            typeof this.sharing().message === 'string' &&
            this.sharing().message.length > 0 &&
            this.sharing().message.trim().length === 0
        );
    }

    /** Verbatim v1 reloadImageTemplate (directive.js:93-119). */
    protected reloadImageTemplate(): void {
        if (this.model().isLoadingTemplate) return;
        const model = {
            SocialApplication: this.parse.int(this.network().socialNetworkType),
            //SelectedPhotoUrl: scope.sharing.background,
            ContestUUID: this.contestUuid(),
            LanguageCode: this.languageCode(),
        };
        this.model().isLoadingTemplate = true;
        this.photoContest
            .getInvitationShareImageTemplate(this.property().UUID, model)
            .then(
                (data: any) => {
                    this.zone.run(() => {
                        this.sharing().template = data.template;
                    });
                },
                () => {
                    this.zone.run(() => {
                        this.sharing().template = '';
                    });
                },
            )
            .finally(() => {
                this.zone.run(() => {
                    this.model().isLoadingTemplate = false;
                    // v1 then $timeout(setPreviewContent) — natively the *ngIf-recreated
                    // iframe leaf writes itself.
                });
            });
    }

    /** Verbatim v1 downloadShareImage (directive.js:121-129) — the template only passes true. */
    protected downloadShareImage(isStayOpen?: boolean): void {
        this.photoContest.downloadShareImage(this.property().UUID, this.sharing().shareImageUrl).then((data: any) => {
            this.fileSaver.saveBlob(data.content, data.fileName);
            if (!isStayOpen) {
                this.zone.run(() => {
                    // v1 hide('invitationShare.shareImageCreated') → its !donePressed '.hide'
                    // handler → on-cancel=showInvitationShareModal → back to the editor.
                    this.activeModal.set('invitationShare');
                });
            }
        });
    }

    /** Verbatim v1 openOAuth (directive.js:138-156). */
    protected openOAuth(): void {
        const sharing = this.sharing(),
            network = this.network(),
            companyCode = this.companyCode();

        const model = {
            contestUUID: this.contestUuid(),
            companyCode: companyCode,
            languageCode: this.languageCode(),
            socialApp: network.socialNetworkType,
            // v1 ng-trim kept the MODEL trimmed — Angular ngModel doesn't, so trim at use.
            shareMessage: typeof sharing.message === 'string' ? sharing.message.trim() : sharing.message,
            shareImageUrl: sharing.shareImageUrl,
            invitationUrl: this.invitationUrl(),
            callback: 'fliptoInvitationShareComplete',
        };
        this.shareOAuth.log('invitation-share', { action: 'open', state: model });
        this.shareOAuth.tryOpenWindow(network.socialNetworkType);
        this.shareOAuth.trySetLocation(network.socialNetworkType, model);
    }

    private translate(key: string): string {
        return this.translateService.instant(key);
    }
}
